/**
 * CompartirPlanesModal.tsx — Arma una imagen con los planes de internet y la comparte.
 *
 * Funciona igual que el comprobante de pago (ComprobanteTicket): se dibuja una
 * "hoja" con html2canvas, se guarda como PNG y se abre el menú de compartir del
 * teléfono (WhatsApp, estados, guardar…). En la web usa el menú de compartir del
 * navegador y, si no lo tiene, descarga el PNG.
 *
 * La hoja se dibuja fuera de la pantalla (portal en <body>, lejos de la animación
 * del modal) a 360 px de ancho y se exporta a escala 3: el PNG sale de 1080 px,
 * el ancho de un estado de WhatsApp. En el modal se ve la imagen ya generada,
 * a pantalla completa en el teléfono, para enseñársela al cliente.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Modal, Spinner } from 'react-bootstrap';
import html2canvas from 'html2canvas';
import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { FaShareAlt, FaWhatsapp } from 'react-icons/fa';
import {
  PLANES_INTERNET, PRECIO_MINIMO, INCLUYE_TODOS, WHATSAPP_VENTAS, WHATSAPP_VENTAS_VISIBLE, SITIO_WEB,
} from '../data/planesInternet';
import './CompartirPlanes.css';

const CompartirIcon = FaShareAlt as unknown as React.FC;
const WhatsAppIcon  = FaWhatsapp as unknown as React.FC;

const ARCHIVO = 'planes_sisnetel.png';
// Carpeta de la caché registrada en android/app/src/main/res/xml/file_paths.xml
const RUTA_CACHE = `sisnetel/${ARCHIVO}`;

// Texto que acompaña a la imagen: en WhatsApp queda como pie de foto con enlaces tocables
const TEXTO_COMPARTIR =
  `Planes de internet por fibra óptica SisNetel Fiber, desde $${PRECIO_MINIMO} al mes con IVA incluido. ` +
  `Escríbenos: https://wa.me/${WHATSAPP_VENTAS} · https://${SITIO_WEB}`;

// Fondo del PNG. html2canvas no resuelve var() en esta opción; es el mismo de .cp-hoja
const FONDO_HOJA = '#12171b';

/** La hoja que se convierte en imagen. Colores fijos a propósito: ver CompartirPlanes.css. */
const HojaPlanes = React.forwardRef<HTMLDivElement>((_, ref) => (
  <div ref={ref} className="cp-hoja" aria-hidden="true">
    <div className="cp-cabecera">
      <img src="/sisnetel.png" alt="" className="cp-logo" />
      <span className="cp-pastilla">IVA incluido</span>
    </div>

    <div className="cp-kicker">Internet de fibra óptica</div>
    <div className="cp-titulo">Planes desde <span>${PRECIO_MINIMO}</span><br />al mes</div>
    <div className="cp-sub">El precio que ves es el que pagas. Sin costo de instalación y con router Wi-Fi en comodato.</div>

    <div className="cp-planes">
      {PLANES_INTERNET.map(plan => (
        <div key={plan.id} className={`cp-plan${plan.etiqueta ? ' cp-plan--destacado' : ''}`}>
          <div className="cp-plan-info">
            <div className="cp-plan-fila">
              <span className="cp-plan-nombre">{plan.nombre}</span>
              {plan.etiqueta && <span className="cp-plan-badge">{plan.etiqueta}</span>}
            </div>
            <div className="cp-plan-frase">{plan.frase}</div>
            <span className="cp-plan-dispositivos">{plan.beneficios[0]}</span>
          </div>
          <div className="cp-plan-precio">
            <strong>${plan.precio}</strong>
            <span>al mes</span>
          </div>
        </div>
      ))}
    </div>

    <div className="cp-incluye">
      <div className="cp-incluye-titulo">Todos los planes incluyen</div>
      <div className="cp-incluye-lista">
        {INCLUYE_TODOS.map(item => (
          <div key={item} className="cp-incluye-item"><span className="cp-punto" />{item}</div>
        ))}
      </div>
    </div>

    <div className="cp-pie">
      <div className="cp-contacto">
        <span className="cp-whatsapp"><WhatsAppIcon /></span>
        <div>
          <div className="cp-contacto-etiqueta">Escríbenos por WhatsApp</div>
          <div className="cp-contacto-numero">{WHATSAPP_VENTAS_VISIBLE}</div>
        </div>
      </div>
      <div className="cp-web">
        <strong>{SITIO_WEB}</strong>
        <span>Manta, Manabí</span>
      </div>
    </div>
  </div>
));

interface CompartirPlanesModalProps {
  show: boolean;
  onHide: () => void;
}

type Estado = 'generando' | 'lista' | 'error';

const CompartirPlanesModal: React.FC<CompartirPlanesModalProps> = ({ show, onHide }) => {
  const hojaRef = useRef<HTMLDivElement | null>(null);
  const [imagen, setImagen] = useState<string | null>(null); // data URL del PNG
  const [estado, setEstado] = useState<Estado>('generando');
  const [compartiendo, setCompartiendo] = useState(false);

  // Los planes son fijos: la imagen se arma una vez y se reutiliza mientras la pantalla siga abierta
  const necesitaHoja = show && !imagen;

  const generar = useCallback(async () => {
    const hoja = hojaRef.current;
    if (!hoja) return;
    setEstado('generando');
    try {
      // Esperar al logo y a las fuentes, o saldrían en blanco en la imagen
      await Promise.all(Array.from(hoja.querySelectorAll('img')).map(img => (
        img.complete ? Promise.resolve() : new Promise<void>(listo => { img.onload = () => listo(); img.onerror = () => listo(); })
      )));
      await document.fonts?.ready;

      const canvas = await html2canvas(hoja, { scale: 3, useCORS: true, backgroundColor: FONDO_HOJA, logging: false });
      setImagen(canvas.toDataURL('image/png'));
      setEstado('lista');
    } catch (e) {
      console.error('Error al armar la imagen de planes:', e);
      setEstado('error');
    }
  }, []);

  useEffect(() => {
    if (necesitaHoja) generar();
  }, [necesitaHoja, generar]);

  const compartir = async () => {
    if (!imagen) return;
    setCompartiendo(true);
    try {
      if (Capacitor.isNativePlatform()) {
        await Filesystem.writeFile({
          path: RUTA_CACHE,
          data: imagen.split(',')[1],
          directory: Directory.Cache,
          recursive: true,
        });
        const { uri } = await Filesystem.getUri({ path: RUTA_CACHE, directory: Directory.Cache });
        await Share.share({ title: 'Planes SisNetel Fiber', text: TEXTO_COMPARTIR, url: uri, dialogTitle: 'Compartir planes' });
      } else {
        const blob = await (await fetch(imagen)).blob();
        const archivo = new File([blob], ARCHIVO, { type: 'image/png' });
        if (navigator.canShare?.({ files: [archivo] })) {
          await navigator.share({ files: [archivo], title: 'Planes SisNetel Fiber', text: TEXTO_COMPARTIR });
        } else {
          const enlace = document.createElement('a');
          enlace.href = imagen;
          enlace.download = ARCHIVO;
          enlace.click();
        }
      }
    } catch (e) {
      // Cerrar el menú de compartir sin elegir nada también llega aquí: no es un fallo
      console.warn('Compartir planes:', e);
    } finally {
      setCompartiendo(false);
    }
  };

  return (
    <>
      {necesitaHoja && createPortal(<HojaPlanes ref={hojaRef} />, document.body)}

      <Modal show={show} onHide={onHide} centered fullscreen="sm-down">
        <Modal.Body className="cp-modal">
          <div>
            <div className="cp-modal-titulo">Planes para compartir</div>
            <div className="cp-modal-sub">Mándala por WhatsApp a quien quieras o enséñala aquí mismo.</div>
          </div>

          <div className="cp-vista">
            {imagen ? (
              <img src={imagen} alt="Planes de internet SisNetel Fiber" />
            ) : estado === 'error' ? (
              <div className="cp-vista-estado">
                No se pudo armar la imagen.
                <button type="button" className="cp-btn cp-btn--secundario" onClick={generar}>Reintentar</button>
              </div>
            ) : (
              <div className="cp-vista-estado">
                <Spinner animation="border" size="sm" />
                Armando la imagen…
              </div>
            )}
          </div>

          <div className="cp-acciones">
            <button
              type="button"
              className="cp-btn cp-btn--principal"
              onClick={compartir}
              disabled={!imagen || compartiendo}
            >
              {compartiendo
                ? <><Spinner animation="border" size="sm" /> Abriendo…</>
                : <><CompartirIcon /> Compartir imagen</>}
            </button>
            <button type="button" className="cp-btn cp-btn--secundario" onClick={onHide}>Cerrar</button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default CompartirPlanesModal;
